// Source step — used by purchased parts (step 1 of 3).
//
// Captures the vendor + SKU + URL + pack size + per-pack price + lead time
// for a component bought-in rather than manufactured. Maps to
// `part.purchasedSource` (PurchasedSource per crmTypes) + the related
// fields on `part.materialCost` (packSize, perPack).
//
// crmTypes PurchasedSource.vendor enum is currently
// 'mcmaster' | 'misumi' | 'grainger' | 'other'. We surface a broader
// dropdown in the UI (MSC, KBC) and fold those into 'other' for storage
// until the enum gets expanded in a future pass.

import { useState } from 'react';
import { NumberInput } from '@/components/NumberInput';
import type { StepProps } from '../stepTypes';
import type { MaterialCost, PurchasedSource } from '@/shared/crmTypes';

type UIVendor = 'McMaster' | 'Misumi' | 'Grainger' | 'MSC' | 'KBC' | 'Other';
const UI_VENDORS: UIVendor[] = ['McMaster', 'Misumi', 'Grainger', 'MSC', 'KBC', 'Other'];

function toSchemaVendor(v: UIVendor): PurchasedSource['vendor'] {
  switch (v) {
    case 'McMaster':
      return 'mcmaster';
    case 'Misumi':
      return 'misumi';
    case 'Grainger':
      return 'grainger';
    default:
      return 'other';
  }
}

function fromSchemaVendor(v: PurchasedSource['vendor'] | undefined): UIVendor {
  switch (v) {
    case 'mcmaster':
      return 'McMaster';
    case 'misumi':
      return 'Misumi';
    case 'grainger':
      return 'Grainger';
    default:
      return 'Other';
  }
}

export function SourceStep({ part, onChange }: StepProps) {
  const [uiVendor, setUiVendor] = useState<UIVendor>(
    fromSchemaVendor(part.purchasedSource?.vendor),
  );

  const ps: PurchasedSource = part.purchasedSource ?? { vendor: 'other' };
  const mc: MaterialCost = part.materialCost ?? {};

  function updateSource(patch: Partial<PurchasedSource>) {
    const next: PurchasedSource = { ...ps, ...patch };
    onChange({ purchasedSource: next });
  }

  function updateMaterialCost(patch: Partial<MaterialCost>) {
    onChange({ materialCost: { ...mc, ...patch, isComponent: true } });
  }

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Vendor">
          <select
            value={uiVendor}
            onChange={(e) => {
              const next = e.target.value as UIVendor;
              setUiVendor(next);
              updateSource({ vendor: toSchemaVendor(next) });
            }}
            className="w-full px-2 py-1.5 border border-line-dark rounded text-sm bg-paper focus:outline-none focus:border-orange"
          >
            {UI_VENDORS.map((v) => (
              <option key={v} value={v}>
                {v}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Vendor part #">
          <input
            type="text"
            value={ps.sku ?? ''}
            onChange={(e) => updateSource({ sku: e.target.value })}
            placeholder="91290A115"
            className="w-full px-2 py-1.5 border border-line-dark rounded text-sm font-mono focus:outline-none focus:border-orange"
          />
        </Field>
      </div>

      <Field label="URL">
        <input
          type="url"
          value={ps.url ?? ''}
          onChange={(e) => updateSource({ url: e.target.value })}
          placeholder="https://www.mcmaster.com/91290A115/"
          className="w-full px-2 py-1.5 border border-line-dark rounded text-sm font-mono focus:outline-none focus:border-orange"
        />
      </Field>

      <div className="grid grid-cols-3 gap-3">
        <Field label="Pack size">
          <NumberInput
            value={mc.packSize}
            onChange={(v) =>
              updateMaterialCost({
                packSize: v === undefined ? undefined : Math.floor(v),
              })
            }
            step={1}
            placeholder="50"
            numpadTitle="Pack size"
          />
        </Field>
        <Field label="Per-pack price ($)">
          <NumberInput
            value={mc.perPack}
            onChange={(v) => updateMaterialCost({ perPack: v })}
            step={1}
            unit="$"
            placeholder="12.50"
            numpadTitle="Per-pack price"
          />
        </Field>
        <Field label="Lead time (days)">
          <input
            type="text"
            value={ps.leadTime ?? ''}
            onChange={(e) => updateSource({ leadTime: e.target.value })}
            placeholder="2"
            className="w-full px-2 py-1.5 border border-line-dark rounded text-sm font-mono focus:outline-none focus:border-orange"
          />
        </Field>
      </div>
    </div>
  );
}

interface FieldProps {
  label: string;
  children: React.ReactNode;
}

function Field({ label, children }: FieldProps) {
  return (
    <label className="block">
      <span className="block text-[10px] font-semibold text-text-mute uppercase tracking-wide mb-1">
        {label}
      </span>
      {children}
    </label>
  );
}
